import { apiClient, apiClient1 } from '@/lib/api-client';
import { API_CONFIG } from '@/config/api';

/**
 * Payment details for Razorpay
 */
export interface RazorpayPaymentDetails {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

/**
 * Order placement request interface
 */
export interface PlaceOrderRequest {
  cart_id: number;
  shipping_address: number;
  billing_address: number;
  payment_status: boolean;
  payment_mode: string;
  payment_details?: RazorpayPaymentDetails | [] | null;
}

/**
 * Order placement response interface
 */
export interface PlaceOrderResponse {
  status: boolean;
  message: string;
  data?: {
    order_id: number;
    order_number: string;
    grand_total: string;
    status: string;
    [key: string]: any;
  };
}

/**
 * Order item interface
 */
export interface OrderItem {
  id: number;
  product_id: number;
  product_name: string;
  product_sku: string;
  quantity: number;
  price: string;
  total: string;
  image?: string;
  [key: string]: any;
}

/**
 * Order interface
 */
export interface Order {
  id: number;
  order_number: string;
  customer_id: number;
  status: string;
  grand_total: string;
  sub_total: string;
  tax_total: string;
  discount_amount: string;
  payment_status: string;
  payment_mode: string;
  shipping_address: any;
  billing_address: any;
  items: OrderItem[];
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

/**
 * Orders list response interface
 */
export interface OrdersListResponse {
  status: boolean;
  message: string;
  data:any;
  meta?: {
    total?: number;
    per_page?: number;
    current_page?: number;
    last_page?: number;
  };
}

/**
 * Get customer orders
 */
export const getOrders = async (customerId?: number,
  page = 1,
  perPage = 10
): Promise<{ orders: any[]; meta: { total: number; per_page: number; current_page: number; last_page: number } }> => {
  try {
    console.log('Fetching orders for customer ID:', customerId,API_CONFIG.ENDPOINTS.ORDERS);
    const params = new URLSearchParams();
    if (customerId) params.append('customer_id', String(customerId));
    params.append('page', String(page));
    params.append('limit', String(perPage));

    const endpoint = customerId 
      ? `${API_CONFIG.ENDPOINTS.ORDERS}?${params.toString()}`
      : `${API_CONFIG.DEFAULT_IMAGE_BASE_URL}${API_CONFIG.ENDPOINTS.ORDERS}?${params.toString()}`;
      console.log('Orders endpoint:', endpoint);
    const response = await apiClient1.get<OrdersListResponse>(endpoint);
         const payload = response;
    let orders: any[] = [];
    let meta = { total: 0, per_page: perPage, current_page: page, last_page: 1 };

    if (Array.isArray(payload)) {
      console.log('Orders payload is an array:', payload);
      orders = payload;
      meta = { total: payload.length, per_page: perPage, current_page: page, last_page: 1 };
    } else if (payload?.data) {
      orders = Array.isArray(payload.data) ? payload.data : payload.data.data ?? [];
      const m = payload.meta ?? payload.data?.meta ?? payload.meta;
      meta = {
        total: m?.total ?? orders.length,
        per_page: m?.per_page ?? perPage,
        current_page: m?.current_page ?? page,
        last_page: m?.last_page ?? Math.max(1, Math.ceil((m?.total ?? orders.length) / perPage)),
      };
    } else {
      orders = [];
    }

    return { orders, meta };
  } catch (error: any) {
    throw new Error(error?.message || 'Get orders failed');
  }
};

export const getOrderById = async (orderId: number | string): Promise<Order> => {
  try {
    // Orders API is at /api/orders level (not /api/v1)
    const endpoint = `/../orders/${orderId}?token=true`;
    
    console.log('Fetching order from:', endpoint);
    
    const response = await apiClient.get<any>(endpoint);
    
    console.log('Order API response:', response);
    
    // API might return order in different formats:
    // 1. { data: Order } - wrapped in data field
    // 2. Order directly - the response itself is the order
    // 3. { data: { data: Order } } - nested data field
    if (response.data) {
      // Check if response.data is an array (from list endpoint) or an object (single order)
      if (Array.isArray(response.data)) {
        // This shouldn't happen for single order, but handle it
        return response.data[0];
      }
      // If response.data has a data field, it might be nested
      if (response.data.data && typeof response.data.data === 'object' && !Array.isArray(response.data.data)) {
        return response.data.data;
      }
      // Otherwise, response.data is the order
      return response.data;
    }
    
    // If no data field, the response itself is the order
    return response as Order;
  } catch (error) {
    console.error('Get order by ID error:', error);
    throw error;
  }
};

/**
 * Place an order
 */
export const placeOrder = async (
  orderData: PlaceOrderRequest
): Promise<PlaceOrderResponse> => {
  try {
    const response = await apiClient.post<PlaceOrderResponse>(
      `${API_CONFIG.ENDPOINTS.ORDERS}/place`,
      orderData
    );
    return response;
  } catch (error) {
    console.error('Order placement error:', error);
    throw error;
  }
};